import React, { useMemo } from 'react';
import { Spin, Alert } from 'antd';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { FinalInspectionReportPrintView } from '@/components/quality/FinalInspectionReportPrintView';
import { finalInspectionReportService } from '@/services/final-inspection-report.service';
import { firRequestService } from '@/services/qc-workflow.service';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';

type FirSource = 'qms_report' | 'fir_request';

function parseSource(raw: string | null): FirSource | null {
  if (raw === 'qms_report' || raw === 'fir_request') return raw;
  return null;
}

const FinalInspectionReportPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resolve } = useUserDisplayResolver();
  const source = parseSource(searchParams.get('source'));

  const printedAt = useMemo(() => new Date().toISOString(), []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['final-inspection-print', id, source],
    queryFn: async () => {
      if (!id || !source) throw new Error('Missing id or source');
      if (source === 'qms_report') {
        return { source, report: await finalInspectionReportService.getById(id) } as const;
      }
      return { source, fir: await firRequestService.getById(id) } as const;
    },
    enabled: !!id && !!source,
  });

  const back = () => {
    if (id && source) navigate(`/qms/final-inspection-reports/${id}?source=${source}`);
    else navigate('/qms/final-inspection-reports');
  };

  if (!id) {
    return <Navigate to="/qms/final-inspection-reports" replace />;
  }

  if (!source) {
    return (
      <PrintPreviewChrome title="Final inspection — print" onBack={back}>
        <div style={{ padding: 48 }}>
          <Alert
            type="error"
            showIcon
            message="Missing source query parameter"
            description='Use ?source=qms_report or ?source=fir_request (same as the detail page).'
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isLoading) {
    return (
      <PrintPreviewChrome title="Final inspection — print preview" onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !data) {
    return (
      <PrintPreviewChrome title="Final inspection — print preview" onBack={back}>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Alert
            type="error"
            showIcon
            message="Could not load record"
            description={(error as Error)?.message || 'Check the id and source match a QMS report or WO FIR request.'}
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  const fileName =
    data.source === 'qms_report' ? data.report.fir_number : data.fir.fir_number;

  return (
    <PrintPreviewChrome title="Final inspection — print preview" fileName={fileName} onBack={back}>
      <div className="print-doc-a4">
        {data.source === 'qms_report' ? (
          <FinalInspectionReportPrintView
            source="qms_report"
            report={data.report}
            printedAt={printedAt}
            resolveUser={resolve}
          />
        ) : (
          <FinalInspectionReportPrintView
            source="fir_request"
            fir={data.fir}
            printedAt={printedAt}
            resolveUser={resolve}
          />
        )}
      </div>
    </PrintPreviewChrome>
  );
};

export default FinalInspectionReportPrintPage;
